import { SearchFilter } from "../../../../common/components/functional/FilterSearchField/FilterSearchField";
import {
  dateToFormatOrEmpty,
  getFiltersFromLocalStorage,
  makeLookupValues,
  makeMultiSelectDisplay,
  mergeInitialAndStoredFilters,
  parseAutocomplete,
  toStringArray,
} from "../../../../common/helpers/filters";
import {
  isoFormat,
  parseIsoDate,
} from "../../../../common/helpers/formatDateTime";
import { NameIdOption } from "../../../../common/types/commonTypes";
import {
  defaultNullableDateRange,
  NullableDateRange,
} from "../../../../common/types/nullable";
import {
  isExpertAdmin,
  isExpertSalaryBillingHr,
} from "../../../user/accessRightChecks";
import { User } from "../../../user/user";
import {
  HR_TASK_LIST_FILTERS_STORAGE_KEY,
  HR_TASK_LIST_PAGE_SIZE,
} from "../constants";
import { HrTasklistQuery, HrTaskType } from "../types";

export type HrTaskFilterCategory = {
  id: string;
  types: HrTaskType[];
};

export const HR_TASK_FILTER_CATEGORIES: readonly HrTaskFilterCategory[] = [
  {
    id: "permitsAndQualifications",
    types: [
      HrTaskType.CertificateExpiring,
      HrTaskType.CertificateApprovalNeeded,
      HrTaskType.ProbationEnding,
    ],
  },
  {
    id: "employments",
    types: [
      HrTaskType.EmploymentEnding,
      HrTaskType.EmploymentHasEnded,
      HrTaskType.WorkAgreementNotAccepted,
      HrTaskType.AcceptedLongWorkAgreement,
    ],
  },
  {
    id: "talentIntroductions",
    types: [HrTaskType.CustomerAnswerToIntroducedTalent],
  },
  {
    id: "ordersAndJobAds",
    types: [HrTaskType.OrderExpired],
  },
  {
    id: "targetedShifts",
    types: [
      HrTaskType.TargetedShiftVariationMismatch,
      HrTaskType.TargetedShiftDidNotFindTarget,
      HrTaskType.TargetedShiftTargetSwitched,
    ],
  },
  {
    id: "absencesAndHourLimits",
    types: [
      HrTaskType.TalentWorkHoursLimitExceeded,
      HrTaskType.SickLeaveShortAbsencesX3,
      HrTaskType.SickLeaveLongAbsence,
      HrTaskType.SickLeaveEntryThreshold,
    ],
  },
  {
    id: "employmentStarts",
    types: [
      HrTaskType.FirstShortEmployment,
      HrTaskType.TenthShortEmployment,
      HrTaskType.NewLongEmployment,
    ],
  },
];

const FILTER_DISPLAY_NAMES = {
  visibilityStartFrom: "hrTasklist.filters.createdFrom",
  visibilityStartTo: "hrTasklist.filters.createdTo",
  categories: "hrTasklist.filters.taskType",
  expertIds: "hrTasklist.filters.handler",
} as const;

export type HrTaskListFilterFormValues = {
  createdRange: NullableDateRange;
  categories: string[];
  experts: NameIdOption[];
};

export const EMPTY_HR_TASK_LIST_FILTER_FORM: HrTaskListFilterFormValues = {
  createdRange: defaultNullableDateRange,
  categories: [],
  experts: [],
};

export function loadHrTaskListFilters(): SearchFilter[] {
  return mergeInitialAndStoredFilters(
    emptyHrTaskListFilters(),
    getFiltersFromLocalStorage(HR_TASK_LIST_FILTERS_STORAGE_KEY),
  );
}

// Salary HR wins over admin, matching HrTaskListQuery.kt
export function allowedHrTaskTypes(user: User | null): HrTaskType[] {
  if (!user) return [];
  if (isExpertSalaryBillingHr(user)) {
    return [HrTaskType.EmploymentHasEnded];
  }
  const allTypes = HR_TASK_FILTER_CATEGORIES.flatMap(
    (category) => category.types,
  );
  if (isExpertAdmin(user)) return allTypes;
  return allTypes.filter((type) => type !== HrTaskType.EmploymentHasEnded);
}

export function visibleHrTaskFilterCategories(
  user: User | null,
): HrTaskFilterCategory[] {
  const allowed = new Set(allowedHrTaskTypes(user));
  return HR_TASK_FILTER_CATEGORIES.map((category) => ({
    id: category.id,
    types: category.types.filter((type) => allowed.has(type)),
  })).filter((category) => category.types.length > 0);
}

export function hrTaskTypesForCategories(
  categoryIds: readonly string[],
  categories: readonly HrTaskFilterCategory[],
): HrTaskType[] {
  const selected = new Set(categoryIds);
  const types: HrTaskType[] = [];
  categories.forEach((category) => {
    if (!selected.has(category.id)) return;
    category.types.forEach((type) => {
      if (!types.includes(type)) types.push(type);
    });
  });
  return types;
}

export function buildHrTaskListFilters(
  values: HrTaskListFilterFormValues,
  categoryLabel: (categoryId: string) => string,
): SearchFilter[] {
  const categoryIds = values.categories.filter((id) =>
    HR_TASK_FILTER_CATEGORIES.some((category) => category.id === id),
  );
  const categoryDisplay = makeMultiSelectDisplay(
    categoryIds.map((id) => ({ name: categoryLabel(id) })),
  );
  const expertDisplay = makeMultiSelectDisplay(values.experts);

  return [
    {
      name: "visibilityStartFrom",
      value: dateToFormatOrEmpty(values.createdRange.from, isoFormat),
      type: "date",
      displayName: FILTER_DISPLAY_NAMES.visibilityStartFrom,
    },
    {
      name: "visibilityStartTo",
      value: dateToFormatOrEmpty(values.createdRange.to, isoFormat),
      type: "date",
      displayName: FILTER_DISPLAY_NAMES.visibilityStartTo,
    },
    {
      name: "categories",
      value: categoryIds,
      type: "text",
      displayName: FILTER_DISPLAY_NAMES.categories,
      displayValue: categoryDisplay.displayValue,
      ...(categoryDisplay.tooltip ? { tooltip: categoryDisplay.tooltip } : {}),
    },
    {
      name: "expertIds",
      value: values.experts.map((expert) => expert.id),
      type: "text",
      displayName: FILTER_DISPLAY_NAMES.expertIds,
      displayValue: expertDisplay.displayValue,
      lookupValues: makeLookupValues(values.experts),
      ...(expertDisplay.tooltip ? { tooltip: expertDisplay.tooltip } : {}),
    },
  ];
}

export function emptyHrTaskListFilters(): SearchFilter[] {
  return buildHrTaskListFilters(EMPTY_HR_TASK_LIST_FILTER_FORM, () => "");
}

export function parseHrTaskListFilters(
  filters: SearchFilter[],
  allowedCategoryIds?: readonly string[],
): HrTaskListFilterFormValues {
  const find = (name: string) => filters.find((filter) => filter.name === name);
  const categoryIds = toStringArray(find("categories")?.value).filter(
    (id) =>
      HR_TASK_FILTER_CATEGORIES.some((category) => category.id === id) &&
      (!allowedCategoryIds || allowedCategoryIds.includes(id)),
  );

  return {
    createdRange: {
      from: parseIsoDate(find("visibilityStartFrom")?.value),
      to: parseIsoDate(find("visibilityStartTo")?.value),
    },
    categories: categoryIds,
    experts: parseAutocomplete(find("expertIds")),
  };
}

// Drops stored category ids the current role cannot query, and updates the chip text.
export function retainVisibleCategories(
  filters: SearchFilter[],
  allowedCategoryIds: readonly string[],
  categoryLabel: (categoryId: string) => string,
): SearchFilter[] {
  const current = filters.find((filter) => filter.name === "categories");
  if (!current) return filters;

  const ids = toStringArray(current.value);
  const visibleIds = ids.filter((id) => allowedCategoryIds.includes(id));
  if (visibleIds.length === ids.length) return filters;

  const { displayValue, tooltip } = makeMultiSelectDisplay(
    visibleIds.map((id) => ({ name: categoryLabel(id) })),
  );

  return filters.map((filter) =>
    filter.name === "categories"
      ? {
          ...filter,
          value: visibleIds,
          displayValue,
          ...(tooltip ? { tooltip } : { tooltip: undefined }),
        }
      : filter,
  );
}

export function toHrTaskListQuery(
  filters: SearchFilter[],
  search: string,
  atDate: string,
  categories: readonly HrTaskFilterCategory[],
): HrTasklistQuery {
  const find = (name: string) => filters.find((filter) => filter.name === name);
  const query: HrTasklistQuery = {
    atDate,
    pageSize: HR_TASK_LIST_PAGE_SIZE,
  };

  const trimmedSearch = search.trim();
  if (trimmedSearch) query.search = trimmedSearch;

  const visibilityStartFrom = stringValue(find("visibilityStartFrom")?.value);
  const visibilityStartTo = stringValue(find("visibilityStartTo")?.value);
  if (visibilityStartFrom) query.visibilityStartFrom = visibilityStartFrom;
  if (visibilityStartTo) query.visibilityStartTo = visibilityStartTo;

  const types = hrTaskTypesForCategories(
    toStringArray(find("categories")?.value),
    categories,
  );
  if (types.length > 0) query.types = types.join(",");

  const expertIds = toStringArray(find("expertIds")?.value);
  if (expertIds.length > 0) query.expertIds = expertIds.join(",");

  return query;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}
